import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, MapPin, Users, Car, Check, Sparkles, CreditCard, Store } from "lucide-react";
import { saveItinerary, pdfUrl } from "@/lib/api";

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground">
    <Icon className="w-4 h-4 text-gold/80 shrink-0" />
    <span>{children}</span>
  </div>
);

export const PricingCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-4 mt-2 max-w-sm" data-testid="pricing-card"
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-gold/70">Experience</p>
        <h4 className="font-display text-xl text-foreground leading-tight">{data.tour}</h4>
      </div>
      <Sparkles className="w-5 h-5 text-gold" />
    </div>
    <div className="mt-3 space-y-1.5">
      <Row icon={MapPin}>{data.zone_label}</Row>
      <Row icon={Users}>{data.pax} guest(s) · {data.vehicle}</Row>
      <Row icon={Car}>{data.vehicle === "prado" ? "Private Land Cruiser Prado" : "Comfort Minibus"}</Row>
    </div>
    <div className="mt-3 pt-3 border-t border-gold/15 flex items-end justify-between">
      <div>
        <p className="text-xs text-muted-foreground">${data.price_per_person} / person</p>
        <p className="font-display text-2xl gold-text" data-testid="pricing-total">${data.total} <span className="text-sm">USD</span></p>
      </div>
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">
      {data.fees_included ? "Includes service, water & all entrance fees." : data.note}
    </p>
  </motion.div>
);

export const TransferCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-4 mt-2 max-w-sm" data-testid="transfer-card"
  >
    <p className="text-[11px] uppercase tracking-[0.2em] text-gold/70">Private Transfer</p>
    <h4 className="font-display text-lg text-foreground leading-tight mt-0.5">{data.label}</h4>
    <div className="mt-2"><Row icon={Car}>{data.vehicle === "prado" ? "Land Cruiser Prado" : data.vehicle === "bus" ? "Coaster Bus" : "Comfort Minibus"} · {data.basis}</Row></div>
    <div className="mt-3 pt-3 border-t border-gold/15">
      <p className="font-display text-2xl gold-text" data-testid="transfer-price">${data.price} <span className="text-sm">USD</span></p>
    </div>
  </motion.div>
);

export const HireCard = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-4 mt-2 max-w-sm" data-testid="hire-card"
  >
    <p className="text-[11px] uppercase tracking-[0.2em] text-gold/70">Vehicle Hire</p>
    <h4 className="font-display text-lg text-foreground mt-0.5 capitalize">{data.vehicle} · {data.duration.replace("_", " ")}</h4>
    <p className="mt-3 font-display text-2xl gold-text">${data.price} <span className="text-sm">USD</span></p>
  </motion.div>
);

export const ItineraryCard = ({ data, onReady, onBook }) => {
  const [saved, setSaved] = useState(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      const payload = {
        id: data.id, title: data.title, guest_name: data.guest_name,
        dates: data.dates, zone: data.zone, pax: data.pax, vehicle: data.vehicle,
        items: (data.items || []).map((it) => ({ day: it.day, tour: it.tour })),
      };
      try {
        const res = await saveItinerary(payload);
        setSaved(res);
        onReady && onReady(res);
      } catch (e) {
        setSaved(data);
        onReady && onReady(data);
      }
    })();
  }, [data, onReady]);

  const it = saved || data;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-5 mt-2 max-w-md" data-testid="itinerary-card"
    >
      <p className="text-[11px] uppercase tracking-[0.2em] text-gold/70">Your Personalised Itinerary</p>
      <h4 className="font-display text-2xl text-foreground leading-tight mt-1">{it.title}</h4>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>{it.guest_name}</span><span>· {it.dates}</span><span>· {it.pax} guest(s)</span><span>· {it.zone_label || it.zone}</span>
      </div>
      <div className="mt-4 space-y-2">
        {(it.items || []).map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-gold/15 text-gold flex items-center justify-center text-xs font-semibold shrink-0">
                {item.day ? `D${item.day}` : i + 1}
              </span>
              <span className="text-sm text-foreground">{item.tour}</span>
            </div>
            <span className="text-sm text-gold/90 shrink-0">${item.total}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-gold/20 flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Total</span>
        <span className="font-display text-3xl gold-text" data-testid="itinerary-total">${it.total} <span className="text-base">USD</span></span>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <a
          href={saved ? pdfUrl(it.id) : undefined}
          target="_blank" rel="noreferrer"
          className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-4 py-2.5 text-sm text-gold hover:bg-gold/10 transition-colors ${saved ? "" : "opacity-50 pointer-events-none"}`}
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4" /> Download PDF
        </a>
        <button
          type="button"
          onClick={() => onBook && onBook(it)}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-2.5 text-sm font-semibold text-background hover:bg-gold-soft transition-colors"
          data-testid="book-and-pay-btn"
        >
          <Check className="w-4 h-4" /> Book &amp; Pay
        </button>
      </div>
    </motion.div>
  );
};

export const PaymentOptionsCard = ({ onChoose }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-2xl p-4 mt-2 max-w-sm" data-testid="payment-options-card"
  >
    <p className="text-sm text-foreground mb-3">How would you like to settle your booking?</p>
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button" onClick={() => onChoose && onChoose("link")}
        className="flex flex-col items-center gap-2 rounded-xl border border-gold/30 p-4 hover:bg-gold/10 transition-colors"
        data-testid="pay-link-btn"
      >
        <CreditCard className="w-6 h-6 text-gold" />
        <span className="text-xs text-foreground">Payment Link</span>
      </button>
      <button
        type="button" onClick={() => onChoose && onChoose("pos")}
        className="flex flex-col items-center gap-2 rounded-xl border border-gold/30 p-4 hover:bg-gold/10 transition-colors"
        data-testid="pay-pos-btn"
      >
        <Store className="w-6 h-6 text-gold" />
        <span className="text-xs text-foreground">POS (In person)</span>
      </button>
    </div>
  </motion.div>
);
